import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';

/**
 * SiteFooter (iter130)
 * ────────────────────────────────────────────────────────────────────────
 * Persistent bottom footer/nav strip that ships on every route.
 *
 * The old implementation was rendered only inside `Homepage.js`, so every
 * other page (listing detail, seller profile, chat, subscriptions, ...)
 * ended in visual dead-space with no quick way to reach Contact / Terms /
 * Privacy — hurting cross-navigation and looking unfinished.
 *
 * We now mount it once in `App.js` beneath `<Routes>` so the exact same
 * strip appears on every applicable route with zero markup duplication.
 *
 * Some routes intentionally hide it:
 *   - /chat/:id conversation panes — they run edge-to-edge on mobile.
 *   - /login  — the auth flow is deliberately chromeless.
 * Any route in HIDDEN_PREFIXES is excluded.
 */

const HIDDEN_PREFIXES = ['/login', '/signup'];

// Chat detail pages need the footer suppressed on mobile only. On desktop
// the reading pane already leaves room for it.
const isChatDetailRoute = (pathname) => /^\/chat\/[^/]+/.test(pathname);

const SiteFooter = () => {
  const { pathname } = useLocation();
  const { t } = useLanguage();

  if (HIDDEN_PREFIXES.some((p) => pathname === p || pathname.startsWith(`${p}/`))) return null;

  // Chat detail on mobile: hide (edge-to-edge reader). Desktop keeps it.
  const hideOnMobileOnly = isChatDetailRoute(pathname);

  return (
    <footer
      data-testid="footer"
      className={`bg-white text-[#1800ad] py-5 border-t border-[#1800ad]/10 ${hideOnMobileOnly ? 'hidden sm:block' : ''}`}
    >
      <div className="max-w-[1440px] mx-auto px-4 sm:px-8 lg:px-12">
        <div className="text-center mb-4">
          <p className="text-base font-bold mb-1">GenzoX Marketplace</p>
          <p className="text-xs">{t('footer_text')}</p>
        </div>
        <div className="pt-3 border-t border-[#1800ad]/10 flex flex-col-reverse sm:flex-row items-center sm:justify-between gap-2.5 text-xs">
          <p data-testid="footer-copyright" className="text-[11px] sm:text-xs tracking-wide text-center sm:text-left">
            Premium Traders &copy; 2026 All Right Reserved.
          </p>
          <div className="flex gap-6">
            <Link to="/contact" data-testid="contact-link" className="hover:underline transition-colors">Contact Us</Link>
            <Link to="/terms" data-testid="terms-link" className="hover:underline transition-colors">Terms &amp; Conditions</Link>
            <Link to="/privacy" data-testid="privacy-link" className="hover:underline transition-colors">Privacy Policy</Link>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default SiteFooter;
